/**
 * DSP API client — wraps all /api/dsp/* endpoints exposed by the SSP via our backend proxy.
 * All calls go through REACT_APP_BACKEND_URL → backend/dsp_proxy.py → SSP.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/dsp`;
const http = axios.create({ timeout: 45000 }); // DNA matching may take a few seconds

export async function getDashboardStats() {
  const { data } = await http.get(`${BASE}/dashboard-stats`);
  return data;
}

export async function discoverCreators(payload) {
  // payload: { campaign_brief, generation?, gender?, is_hbcu?, school?, niche_filter?, min_followers?, min_engagement?, top_n?, vertical?, brand_id? }
  // 1. Try DNA-based 6-vector match on the SSP first (auth required via HTTP-only cookies).
  // 2. If SSP DNA isn't available (disabled, unreachable, or the endpoint hasn't shipped)
  //    fall back to the existing DSP keyword-based discovery.
  try {
    const { data: dna } = await axios.post(
      `${API}/api/brand/dna/match-creators`,
      { top_n: payload?.top_n ?? 12, filters: payload?.filters ?? null },
      { withCredentials: true, timeout: 15000 },
    );
    if (dna && dna.ok && Array.isArray(dna.creators) && dna.creators.length > 0) {
      return { creators: dna.creators, source: 'dna' };
    }
    // dna.ok=false → SSP returned a graceful no-op (disabled/unreachable/404). Fall through.
  } catch (dna_error) {
    // Network / 5xx / 401 / timeout → log so we know the DNA path silently
    // failed (vs. the keyword fallback firing), then fall through.
    if (process.env.NODE_ENV !== 'production') {
      // Use info() not error() since this is a non-fatal expected fallback.
      console.info('[discoverCreators] DNA path failed, using keyword fallback:', dna_error?.message || dna_error);
    }
  }
  const { data } = await http.post(`${BASE}/discover-creators`, payload);
  return { ...data, source: data?.source || 'keyword' };
}

export async function getSspHealth() {
  const { data } = await axios.get(`${API}/api/brand/ssp/health`, { withCredentials: true, timeout: 6000 });
  return data;
}

export async function explainMatch({ creatorEmail, brandId = null }) {
  // Hit the SSP-DNA /explain endpoint via our backend proxy. Returns the full
  // 7-component breakdown plus bonuses, penalties, and the semantic_used flag.
  const body = { creator_email: creatorEmail };
  if (brandId) body.brand_id = brandId;
  const { data } = await axios.post(`${API}/api/brand/dna/explain`, body, { withCredentials: true, timeout: 12000 });
  return data;  // { ok, data: {dna_score, components, bonuses, penalties, semantic_used, ...} }
}

export async function getTentpoleCalendar({ monthsAhead = 6, vertical = null } = {}) {
  const params = { months_ahead: monthsAhead };
  if (vertical) params.vertical = vertical;
  const { data } = await http.get(`${BASE}/tentpole-calendar`, { params });
  return data;
}

export async function getVerticals() {
  const { data } = await http.get(`${BASE}/tentpole-calendar/verticals`);
  return data;
}

export async function getTentpoleTemplate(templateId) {
  const { data } = await http.get(`${BASE}/tentpole-calendar/${encodeURIComponent(templateId)}`);
  return data;
}

export async function getCampusTemplates() {
  const { data } = await http.get(`${BASE}/campus-templates`);
  return data;
}

export async function activateTentpoleWave(payload) {
  const { data } = await http.post(`${BASE}/activate-tentpole-wave`, payload);
  return data;
}

export async function activateCampusWave(payload) {
  const { data } = await http.post(`${BASE}/activate-campus-wave`, payload);
  return data;
}

export async function buildCustomWave(payload) {
  const { data } = await http.post(`${BASE}/build-wave`, payload);
  return data;
}

export async function getCreatorProfile(handle) {
  // handle includes '@'. Server expects URL-encoded value.
  const { data } = await http.get(`${BASE}/creator/${encodeURIComponent(handle)}`);
  return data;
}

export async function searchContent(payload) {
  // payload: { query, platform?, category?, creator_handle?, top_n? }
  const { data } = await http.post(`${BASE}/search-content`, payload);
  return data;
}

export const GENERATION_LABELS = {
  gen_z: 'Gen Z',
  millennial: 'Millennial',
  gen_x: 'Gen X',
  boomer: 'Baby Boomer',
};

export const GENERATIONS = [
  { value: 'gen_z', label: 'Gen Z (18-28)' },
  { value: 'millennial', label: 'Millennial (29-44)' },
  { value: 'gen_x', label: 'Gen X (45-60)' },
  { value: 'boomer', label: 'Baby Boomer (61+)' },
];

export const GENDERS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];
